import { normalizeGoogleAdsCustomerId } from '@/utils/googleAdsUtils'
import { CheckCircle, Edit, ExternalLink, Plus } from 'lucide-react'
import { SiGoogleads } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsCampaignManagement,
  type GoogleAdsCampaignManagementResult,
} from 'sprites-types/workflow/tools/googleAdsCampaignManagement'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GoogleAdsCampaignManagementDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsCampaignManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsCampaignManagementResult
  const isCreate = 'alreadyExisted' in result && !result.alreadyExisted
  const name = 'name' in result ? result.name : undefined
  const status = 'status' in result ? result.status : undefined
  const campaignId = 'campaignId' in result ? result.campaignId : undefined
  const dailyBudget = 'dailyBudget' in result ? result.dailyBudget : undefined
  const bidStrategy = 'bidStrategy' in result ? result.bidStrategy : undefined
  const targetCpa = 'targetCpa' in result ? result.targetCpa : undefined
  const targetRoas = 'targetRoas' in result ? result.targetRoas : undefined
  const campaignType = 'campaignType' in result ? result.campaignType : undefined
  const startDate = 'startDate' in result ? result.startDate : undefined
  const endDate = 'endDate' in result ? result.endDate : undefined
  const adGroupCount = 'adGroupCount' in result ? result.adGroupCount : undefined
  const assetGroupCount = 'assetGroupCount' in result ? result.assetGroupCount : undefined
  const updatedFields = 'updatedFields' in result ? result.updatedFields : undefined

  // Extract customerId from inputs for Google Ads link
  const inputs = nodeExecution.inputs.functionArgs as any
  const customerId = inputs?.action?.params?.customerId

  // Normalize customer ID for Google Ads URL (removes dashes and "customers/" prefix)
  let normalizedCustomerId: string | undefined
  if (customerId) {
    try {
      normalizedCustomerId = normalizeGoogleAdsCustomerId(customerId)
    } catch {
      // If normalization fails, normalizedCustomerId remains undefined and link won't render
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const formatBidStrategy = (strategy: string | number | undefined) => {
    if (!strategy) return ''

    // Backend already converts numeric enums to strings via convertBiddingStrategyType()
    // So we only need to format the string (e.g., "MANUAL_CPC" -> "Manual CPC")
    const strategyString = String(strategy)

    // If it's already formatted (no underscores), return as-is
    if (!strategyString.includes('_')) {
      return strategyString
    }

    // Format underscore-separated strings to title case
    return strategyString
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')
  }

  const formatCampaignType = (type: string | undefined) => {
    if (!type) return ''
    // Format underscore-separated strings to title case
    return type
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')
  }

  const formatDate = (date: string | undefined) => {
    if (!date) return ''
    try {
      const dateObj = new Date(date)
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return date
    }
  }

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-3">
        <div className="mt-1 flex-shrink-0">
          <div className="rounded-full bg-blue-100 p-2">
            {isCreate ? (
              <Plus className="h-5 w-5 text-blue-600" />
            ) : (
              <Edit className="h-5 w-5 text-blue-600" />
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <SiGoogleads className="h-4 w-4 text-[#4285f4]" />
            <div className="text-sm font-semibold text-gray-900">
              {isCreate ? 'Campaign Created' : 'Campaign Updated'}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
            {name && <div className="mb-1 text-sm font-medium text-gray-900">{name}</div>}
            <div className="space-y-1 text-xs">
              {campaignId && <div className="text-gray-500">Campaign ID: {campaignId}</div>}
              {dailyBudget !== undefined && (
                <div className="text-gray-700">
                  Daily Budget: <span className="font-medium">{formatCurrency(dailyBudget)}</span>
                </div>
              )}
              {bidStrategy && (
                <div className="text-gray-700">
                  Bidding Strategy:{' '}
                  <span className="font-medium">{formatBidStrategy(bidStrategy)}</span>
                  {bidStrategy === 'TARGET_CPA' && targetCpa != null && (
                    <span className="ml-1 text-gray-600">
                      (Target: {formatCurrency(targetCpa)})
                    </span>
                  )}
                  {bidStrategy === 'TARGET_ROAS' && targetRoas !== undefined && (
                    <span className="ml-1 text-gray-600">(Target: {targetRoas}x)</span>
                  )}
                </div>
              )}
              {campaignType && (
                <div className="text-gray-700">
                  Campaign Type:{' '}
                  <span className="font-medium">{formatCampaignType(campaignType)}</span>
                </div>
              )}
              {(startDate || endDate) && (
                <div className="text-gray-700">
                  {startDate && (
                    <span>
                      Start: <span className="font-medium">{formatDate(startDate)}</span>
                    </span>
                  )}
                  {startDate && endDate && <span className="mx-1">•</span>}
                  {endDate && (
                    <span>
                      End: <span className="font-medium">{formatDate(endDate)}</span>
                    </span>
                  )}
                </div>
              )}
              {(adGroupCount !== undefined || assetGroupCount !== undefined) && (
                <div className="text-gray-700">
                  {adGroupCount !== undefined && (
                    <span>
                      Ad Groups: <span className="font-medium">{adGroupCount}</span>
                    </span>
                  )}
                  {assetGroupCount !== undefined && (
                    <span>
                      Asset Groups: <span className="font-medium">{assetGroupCount}</span>
                    </span>
                  )}
                </div>
              )}
              {status && (
                <div className="mt-1">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      status === 'ENABLED'
                        ? 'bg-green-100 text-green-800'
                        : status === 'PAUSED'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {status}
                  </span>
                </div>
              )}
              {updatedFields && updatedFields.length > 0 && (
                <div className="mt-2 text-gray-700">
                  <div className="font-medium">Updated Fields:</div>
                  <div className="mt-0.5 text-gray-600">
                    {updatedFields.map((field, index) => (
                      <span key={field}>
                        {field}
                        {index < updatedFields.length - 1 && ', '}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {normalizedCustomerId && campaignId && (
                <div className="mt-2 border-t border-gray-200 pt-2">
                  <a
                    href={`https://ads.google.com/aw/campaigns?campaignId=${campaignId}&__c=${normalizedCustomerId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                  >
                    View in Google Ads
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </div>
          </div>

          {(isCreate || (updatedFields && updatedFields.length > 0)) && (
            <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
              <CheckCircle className="h-3 w-3 text-green-600" />
              <span>
                {isCreate
                  ? 'Campaign is ready to use'
                  : `Campaign updated successfully (${updatedFields?.length || 0} field${(updatedFields?.length || 0) !== 1 ? 's' : ''} changed)`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function downloadGoogleAdsCampaignManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsCampaignManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsCampaignManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as GoogleAdsCampaignManagementResult
  const isCreate = 'alreadyExisted' in result && !result.alreadyExisted
  const name = 'name' in result ? result.name : undefined
  const status = 'status' in result ? result.status : undefined
  const campaignId = 'campaignId' in result ? result.campaignId : undefined

  return `## Google Ads Campaign ${isCreate ? 'Created' : 'Updated'}

${name ? `**Name:** ${name}` : ''}
${campaignId ? `**Campaign ID:** ${campaignId}` : ''}
${status ? `**Status:** ${status}` : ''}

---
`
}

export const GoogleAdsCampaignManagementDisplay = createWorkflowToolComponent(
  GoogleAdsCampaignManagementDisplayComponent,
  downloadGoogleAdsCampaignManagement,
  googleAdsCampaignManagementToMarkdown
)
